import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { v4 as uuid } from 'uuid'
//Store
import { useAnnotationsStore } from './annotationsStore'
//Types
import type { Ref } from 'vue'
import {
  ImageMeasurement,
  ImageMeasurementType,
  ImageMeasurements,
} from 'Modules/types/pages/imageReview/imageMeasurementType'

export const useMeasurementStore = defineStore('ImageReview_Measurements', () => {
  const annotationsStore = useAnnotationsStore()
  const { currentAnnotation } = storeToRefs(annotationsStore)

  const measurements: Ref<ImageMeasurements> = ref([])
  const currentMeasurement: Ref<ImageMeasurementType> = ref(undefined)
  const currentHoveredMeasurement: Ref<ImageMeasurementType> = ref(undefined)

 const currentAnnotationMeasurements = computed<ImageMeasurements>(() => {
  const currentMeasurementValue = currentMeasurement?.value;
  const currentAnnotationValue = currentAnnotation?.value;

  // if currentMeasurement.value OR currentAnnotation.value is null or undefined, return an empty array.
  if (currentMeasurementValue == null && currentAnnotationValue == null) {
    return [];
  }

  const targetMeasurementUuid = currentMeasurementValue?.annotationUuid;
  const targetAnnotationUuid = currentAnnotationValue?.annotationUuid;

  if (targetMeasurementUuid == null && targetAnnotationUuid == null) {
    return [];
  }

  return (
    measurements.value?.filter((measurement) => {
      if (!measurement?.annotationUuid) {
        return false;
      }
      const individualMeasurementUuid = measurement.annotationUuid;

      let matches = false;

      if (targetMeasurementUuid != null) {
        if (individualMeasurementUuid === targetMeasurementUuid) {
          matches = true;
        }
      }

      if (!matches && targetAnnotationUuid != null) {
        if (individualMeasurementUuid === targetAnnotationUuid) {
          matches = true;
        }
      }

      return matches;
    }) || []
  );
});


  function setMeasurements(measurementsData: Array<ImageMeasurementType>) {
    measurements.value = measurementsData
  }

  function setCurrentMeasurement(newMeasurement?: ImageMeasurementType) {
    currentMeasurement.value = newMeasurement
  }

  function setCurrentHoveredMeasurement(newMeasurement?: ImageMeasurementType) {
    currentHoveredMeasurement.value = newMeasurement
  }

  function setCurrentHoveredMeasurementById(measurementUuid: string) {
    const measurement = measurements.value?.find(
      (measurement: ImageMeasurementType) => measurement.measurementUuid === measurementUuid
    )
    setCurrentHoveredMeasurement(measurement)
  }

  function addTempMeasurement(points: Array<Array<number>>, annotationUuid: string) {
    const id = `temp-${uuid()}`
    const measurement = new ImageMeasurement(id, annotationUuid, points, null, null, null)
    measurements.value = [...measurements.value, measurement]
    setCurrentMeasurement(measurement)
    return id
  }

  function removeTempMeasurement() {
    measurements.value = measurements.value.filter((measurement: ImageMeasurementType) => {
      const isTempMeasurement = measurement?.measurementUuid.includes('temp')
      return !isTempMeasurement
    })
  }

  function updateMeasurement(newMeasurementValues: ImageMeasurementType) {
    measurements.value = measurements.value.map((measurement) => {
      if (measurement.measurementUuid === newMeasurementValues.measurementUuid) return newMeasurementValues
      else return measurement
    })
  }

  function editMeasurementPointAndSetToCurrent(uuid: string, points: Array<Array<number>>) {
    const matchingMeasurement = measurements.value.find(
      (measurement: ImageMeasurementType) => measurement.measurementUuid === uuid
    )
    setCurrentMeasurement({
      ...matchingMeasurement,
      points,
    })
  }

  function setMeasurementUuid(currentUuid: string, newUuid: string) {
    const index = measurements.value.findIndex((measurement: ImageMeasurementType) => {
      return measurement.measurementUuid === currentUuid
    })
    if (index >= 0) measurements.value[index].measurementUuid = newUuid
  }

  function removeMeasurement(measurement: ImageMeasurementType) {
    measurements.value = measurements.value.filter((_measurement: ImageMeasurementType) => {
      return _measurement.measurementUuid !== measurement.measurementUuid
    })
  }

  function removeMeasurementByUuid(uuid: string) {
    measurements.value = measurements.value.filter((measurement: ImageMeasurementType) => {
      return measurement.measurementUuid !== uuid
    })
  }

  function resetAllMeasurementState() {
    measurements.value = []
    currentMeasurement.value = null
    currentHoveredMeasurement.value = null
  }

  function getMeasurementByUuid(measurementUuid: string) {
    return measurements.value.find((measurement) => measurement.measurementUuid === measurementUuid)
  }

  function revertCurrentMeasurementToOriginal() {
    if (currentMeasurement.value) {
      const originalMeasurement = getMeasurementByUuid(currentMeasurement.value.measurementUuid)
      currentMeasurement.value = originalMeasurement
    }
  }

  return {
    measurements,
    currentAnnotationMeasurements,
    currentMeasurement,
    currentHoveredMeasurement,
    setMeasurements,
    setCurrentMeasurement,
    setCurrentHoveredMeasurement,
    setCurrentHoveredMeasurementById,
    addTempMeasurement,
    updateMeasurement,
    removeMeasurement,
    editMeasurementPointAndSetToCurrent,
    setMeasurementUuid,
    removeMeasurementByUuid,
    removeTempMeasurement,
    resetAllMeasurementState,
    getMeasurementByUuid,
    revertCurrentMeasurementToOriginal,
  }
})
